<template>
    <Head title="Get Started - RentRisk" />

    <div class="min-h-screen bg-white flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div class="w-full flex flex-col items-center">
            <!-- Form Container -->
            <div class="max-w-lg w-full">
                <div class="bg-white rounded-lg shadow-lg p-6">
                <!-- Logo -->
                <div class="flex justify-center items-center mb-6">
                    <jet-authentication-card-logo-colored />
                </div>
                <!-- Header -->
                <div class="text-center mb-6">
                    <h1 class="text-2xl font-bold text-gray-900 mb-1 flex items-center justify-center gap-2">
                        Welcome to RentRisk! 
                        <font-awesome-icon :icon="faPartyHorn" class="text-yellow-600 text-xl" />
                    </h1>
                    <p class="text-gray-600 text-sm mt-1">
                        Please fill out your account details below
                    </p>
                </div>

                <jet-validation-errors class="mb-3" />

                <p v-if="page.props.flash?.error" class="mb-3 text-sm text-red-600 text-center">
                    {{ page.props.flash.error }}
                </p>

                <form @submit.prevent="submit" class="space-y-4">
                    <!-- User Role (RAC-9763: moved above the rest of the form) -->
                    <div>
                        <label for="user_role" class="block text-sm font-medium text-gray-700 mb-1">
                            User Role
                        </label>
                        <div class="relative">
                            <select
                                id="user_role"
                                v-model="form.account_type"
                                :disabled="roleDisabled"
                                required
                                :class="[
                                    'mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-2 border appearance-none pr-10',
                                    roleDisabled ? 'bg-gray-100 cursor-not-allowed text-gray-600' : 'bg-white'
                                ]"
                            >
                                <option value="" disabled>Select your role</option>
                                <option value="agent">Manager — I manage one or more rental properties myself</option>
                                <option value="applicant">Renter — I'm checking my credit score or applying for a rental property</option>
                                <option v-if="showEnterpriseOption" value="enterprise">Enterprise — We have 2 or more people managing a large rental portfolio</option>
                            </select>
                        </div>
                        <p v-if="form.errors.account_type" class="mt-1 text-sm text-red-600">{{ form.errors.account_type }}</p>
                    </div>

                    <!-- Enterprise company details (RAC-9763) -->
                    <template v-if="isEnterpriseRole">
                        <div>
                            <label for="company_name" class="block text-sm font-medium text-gray-700 mb-1">
                                Company name <span class="text-red-500">*</span>
                            </label>
                            <input
                                id="company_name"
                                type="text"
                                v-model="form.company_name"
                                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-2 border"
                            />
                            <p v-if="form.errors.company_name" class="mt-1 text-sm text-red-600">{{ form.errors.company_name }}</p>
                        </div>

                        <div>
                            <label for="company_website" class="block text-sm font-medium text-gray-700 mb-1">
                                Company website <span class="text-gray-400 font-normal">(optional)</span>
                            </label>
                            <input
                                id="company_website"
                                type="url"
                                v-model="form.company_website"
                                placeholder="https://www.example.com"
                                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-2 border"
                            />
                            <p class="mt-1 text-xs text-gray-500">Include https:// or http:// (e.g. https://rentrisk.com).</p>
                            <p v-if="form.errors.company_website" class="mt-1 text-sm text-red-600">{{ form.errors.company_website }}</p>
                        </div>

                        <div>
                            <label for="company_email" class="block text-sm font-medium text-gray-700 mb-1">
                                Company email <span class="text-red-500">*</span>
                            </label>
                            <input
                                id="company_email"
                                type="email"
                                v-model="form.company_email"
                                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-2 border"
                            />
                            <p v-if="form.errors.company_email" class="mt-1 text-sm text-red-600">{{ form.errors.company_email }}</p>
                        </div>

                        <div>
                            <label for="company_address" class="block text-sm font-medium text-gray-700 mb-1">
                                Company address <span class="text-red-500">*</span>
                            </label>
                            <input
                                id="company_address"
                                type="text"
                                v-model="form.company_address"
                                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-2 border"
                            />
                            <p v-if="form.errors.company_address" class="mt-1 text-sm text-red-600">{{ form.errors.company_address }}</p>
                        </div>

                        <div>
                            <label for="num_property_managers" class="block text-sm font-medium text-gray-700 mb-1">
                                Number of property managers at your company <span class="text-red-500">*</span>
                            </label>
                            <input
                                id="num_property_managers"
                                type="number"
                                min="1"
                                v-model="form.num_property_managers"
                                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-2 border"
                            />
                            <p v-if="form.errors.num_property_managers" class="mt-1 text-sm text-red-600">{{ form.errors.num_property_managers }}</p>
                        </div>
                    </template>

                    <!-- First Name and Last Name Row -->
                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label for="first_name" class="block text-sm font-medium text-gray-700 mb-1">
                                First name
                            </label>
                            <input
                                id="first_name"
                                type="text"
                                v-model="form.first_name"
                                required
                                autofocus
                                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-2 border"
                            />
                            <p v-if="form.errors.first_name" class="mt-1 text-sm text-red-600">{{ form.errors.first_name }}</p>
                        </div>

                        <div>
                            <label for="last_name" class="block text-sm font-medium text-gray-700 mb-1">
                                Last name
                            </label>
                            <input
                                id="last_name"
                                type="text"
                                v-model="form.last_name"
                                required
                                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-2 border"
                            />
                            <p v-if="form.errors.last_name" class="mt-1 text-sm text-red-600">{{ form.errors.last_name }}</p>
                        </div>
                    </div>

                    <!-- Email Address -->
                    <div>
                        <label for="email" class="block text-sm font-medium text-gray-700 mb-1">
                            Email address
                        </label>
                        <div class="relative">
                            <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <font-awesome-icon :icon="faEnvelope" class="h-5 w-5 text-gray-400" />
                            </div>
                            <input
                                id="email"
                                type="email"
                                v-model="form.email"
                                :disabled="!!invitationEmail"
                                :readonly="!!invitationEmail"
                                required
                                autocomplete="email"
                                :class="[
                                    'mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm pl-10 pr-3 py-2 border',
                                    invitationEmail ? 'bg-gray-100 cursor-not-allowed' : ''
                                ]"
                            />
                        </div>
                        <p v-if="form.errors.email" class="mt-1 text-sm text-red-600">{{ form.errors.email }}</p>
                        <p v-if="invitationEmail" class="mt-1 text-sm text-gray-500">
                            Registering with invited email: <span class="font-medium">{{ invitationEmail }}</span>
                        </p>
                    </div>

                    <!-- Phone Number -->
                    <div>
                        <label for="phone" class="block text-sm font-medium text-gray-700 mb-1">
                            Phone Number <span class="text-red-500">*</span>
                        </label>
                        <div class="relative">
                            <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <img src="https://flagcdn.com/w20/us.png" alt="US" class="h-4 w-6 object-cover" />
                            </div>
                            <input
                                id="phone"
                                type="tel"
                                v-model="form.phone"
                                required
                                autocomplete="tel"
                                pattern="[0-9]{10}"
                                placeholder="2125556789"
                                maxlength="10"
                                @input="formatPhone"
                                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm pl-12 pr-10 py-2 border"
                            />
                        </div>
                        <p v-if="form.errors.phone" class="mt-1 text-sm text-red-600">{{ form.errors.phone }}</p>
                    </div>

                    <!-- RAC-10004: password + confirm fields. User sets
                         password at signup; no more set-password-via-email. -->
                    <div>
                        <label for="password" class="block text-sm font-medium text-gray-700 mb-1">
                            Password
                        </label>
                        <input
                            id="password"
                            type="password"
                            v-model="form.password"
                            required
                            autocomplete="new-password"
                            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-2 border"
                        />
                        <p v-if="form.errors.password" class="mt-1 text-sm text-red-600">{{ form.errors.password }}</p>
                        <p class="mt-1 text-xs text-gray-500">
                            At least 8 characters with mixed case, a number, and a symbol.
                        </p>
                    </div>

                    <div>
                        <label for="password_confirmation" class="block text-sm font-medium text-gray-700 mb-1">
                            Confirm Password
                        </label>
                        <input
                            id="password_confirmation"
                            type="password"
                            v-model="form.password_confirmation"
                            required
                            autocomplete="new-password"
                            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-2 border"
                        />
                    </div>

                    <!-- Terms Checkbox -->
                    <div v-if="hasTermsFeature" class="flex items-start">
                        <input
                            id="terms"
                            type="checkbox"
                            v-model="form.terms"
                            required
                            class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-1"
                        />
                        <label for="terms" class="ml-2 block text-sm text-gray-700">
                            By creating an account, you agree to our
                            <a :href="route('terms-and-conditions')" target="_blank" class="text-blue-600 hover:text-blue-800 underline">Terms of Service</a>
                            and
                            <a :href="route('privacy-policy')" target="_blank" class="text-blue-600 hover:text-blue-800 underline">Privacy Policy</a>
                        </label>
                    </div>

                    <!-- RAC-10004: Cloudflare Turnstile bot defense.
                         The widget injects cf-turnstile-response into the
                         form on success. Server verifies via TurnstileService. -->
                    <div v-if="turnstileSiteKey" class="flex justify-center">
                        <div ref="turnstileEl"></div>
                    </div>
                    <p v-if="form.errors.cf_turnstile_response" class="text-sm text-red-600 text-center">
                        {{ form.errors.cf_turnstile_response }}
                    </p>

                    <!-- Submit Button -->
                    <div class="pt-2">
                        <button
                            type="submit"
                            :disabled="form.processing"
                            class="w-full flex items-center justify-center gap-2 theme_color hover:opacity-90 text-white font-semibold py-2.5 px-4 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#2A375E] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            Let's Get Started!
                            <font-awesome-icon :icon="faPaperPlane" class="h-4 w-4" />
                        </button>
                    </div>
                </form>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { Head, useForm, usePage } from '@inertiajs/vue3'
import JetValidationErrors from '@/Jetstream/ValidationErrorsWithHtml.vue'
import JetAuthenticationCardLogoColored from '@/Jetstream/AuthenticationCardLogoColored.vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { computed, onMounted, onBeforeUnmount, ref } from 'vue'
import { faPartyHorn } from '@fortawesome/pro-light-svg-icons'
import { faEnvelope, faPaperPlane } from '@fortawesome/pro-solid-svg-icons'

const page = usePage()

const enterpriseRegistrationEnabled = computed(() => Boolean(page.props.enterprise_registration_enabled))

// RAC-10004: Turnstile site key from Vite env (inlined at build time).
// Empty string when unset → widget hides + form proceeds without
// bot defense (acceptable for local dev; production must set the key).
const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY || ''

// Get props from page (passed from controller)
const affiliateShareHash = page.props.affiliate_share_hash || null
const defaultRole = page.props.default_role || null // 'agent' or 'applicant' or null
const invitationToken = page.props.invitation_token || null
const invitationEmail = page.props.invitation_email || null
const roleDisabled = page.props.role_disabled || false

// Determine account type - use invitation role if available, otherwise use defaultRole
const initialAccountType = invitationToken && invitationEmail
    ? 'applicant'
    : (defaultRole || (invitationEmail ? 'agent' : '') || '')

// RAC-10417 removed Renter and Enterprise as organic choices here. 86bc1ymu6
// puts Renter back: self credit check gives a renter a reason to sign up with
// no property and no invitation, so the option has to stand on its own again.
// Enterprise stays gated — it renders only when linked with ?role=enterprise
// (the pricing page) while the registration feature flag is on.
const showEnterpriseOption = computed(() => enterpriseRegistrationEnabled.value && initialAccountType === 'enterprise')

const form = useForm({
    first_name: '',
    last_name: '',
    email: invitationEmail || '', // Pre-populate with invitation email if available
    phone: '',
    account_type: initialAccountType, // Set default based on role parameter or invitation
    name: '', // Will be combined from first_name and last_name
    password: '',
    password_confirmation: '',
    cf_turnstile_response: '',
    terms: false,
    affiliate_share_hash: affiliateShareHash,
    invitation_token: invitationToken, // Include invitation token in form
    // RAC-9763: Enterprise-only company fields (sent only when the
    // Enterprise role is selected; ignored by the backend otherwise).
    company_name: '',
    company_website: '',
    company_email: '',
    company_address: '',
    num_property_managers: '',
})

// RAC-10004: Turnstile loads its widget script async. We render the
// widget explicitly (not implicit class="cf-turnstile") so we hold a
// widgetId and can reset() it after a failed submit — Turnstile tokens
// are single-use, so a spent token must be re-armed before the next
// attempt or the user loops on "Human verification failed."
let turnstileScriptEl = null
const turnstileEl = ref(null)
let widgetId = null

const renderTurnstile = () => {
    if (!turnstileSiteKey || !window.turnstile || !turnstileEl.value) { return }

    // retry:'auto' and refresh-expired:'auto' are Cloudflare's documented
    // self-healing options (both are Cloudflare's own defaults; set explicitly
    // so the behavior can't silently change): on a network/challenge error
    // Turnstile retries on its own, and an expired token is refreshed
    // automatically — so a legitimate user is never stranded without a usable
    // token and can never be hard-blocked from registering. The error/expired
    // callbacks independently clear the dead token from the form so it is never
    // submitted; the widget's own auto-retry/refresh then fires `callback`
    // again with a fresh token and repopulates the field.
    widgetId = window.turnstile.render(turnstileEl.value, {
        sitekey: turnstileSiteKey,
        retry: 'auto',
        'retry-interval': 8000,
        'refresh-expired': 'auto',
        callback: (token) => { form.cf_turnstile_response = token },
        'error-callback': () => { form.cf_turnstile_response = '' },
        'expired-callback': () => { form.cf_turnstile_response = '' },
    })
}

onMounted(() => {
    if (!turnstileSiteKey) { return }

    // Turnstile's API is already loaded (e.g. a prior Inertia page visit):
    // render immediately. Otherwise expose the onload hook the script calls
    // when ready, then inject the script once in explicit-render mode.
    if (window.turnstile) {
        renderTurnstile()
        return
    }

    window.onloadTurnstileCallback = renderTurnstile

    if (!document.querySelector('script[src*="challenges.cloudflare.com/turnstile"]')) {
        turnstileScriptEl = document.createElement('script')
        turnstileScriptEl.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=onloadTurnstileCallback'
        turnstileScriptEl.async = true
        turnstileScriptEl.defer = true
        document.head.appendChild(turnstileScriptEl)
    }
})

onBeforeUnmount(() => {
    if (widgetId !== null && window.turnstile) {
        try { window.turnstile.remove(widgetId) } catch (e) { /* widget already gone */ }
    }
    widgetId = null
    delete window.onloadTurnstileCallback
})

// RAC-9763: show the Enterprise company fields when the Enterprise role is picked
const isEnterpriseRole = computed(() => form.account_type === 'enterprise')

const hasTermsFeature = computed(() => page.props.jetstream?.hasTermsAndPrivacyPolicyFeature)

const formatPhone = (event) => {
    // Remove all non-numeric characters
    const value = event.target.value.replace(/\D/g, '')
    // Limit to 10 digits
    form.phone = value.slice(0, 10)
}

const submit = () => {
    // Combine first_name and last_name into name for backend compatibility
    form.name = `${form.first_name} ${form.last_name}`.trim()

    // Ensure phone is exactly 10 digits (strip any remaining non-numeric chars)
    form.phone = form.phone.replace(/\D/g, '').slice(0, 10)

    form.post(route('get-started.store'), {
        // Re-arm Turnstile after EVERY completed attempt. Tokens are
        // single-use, so any non-success — a validation error, a downstream
        // exception, a token Cloudflare rejected, a 429 throttle, or a
        // network failure — leaves a spent/stale token in the form and the
        // user would loop on "Human verification failed." onFinish fires for
        // all of those; onError only fires for validation errors, so it
        // misses network failures and non-422 responses. On the success path
        // this is a harmless no-op since we redirect away, and the widgetId
        // guard covers the case where the component has already unmounted.
        onFinish: () => {
            form.cf_turnstile_response = ''
            if (widgetId !== null && window.turnstile) {
                window.turnstile.reset(widgetId)
            }
        },
    })
}
</script>
